import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
  IconButton,
  Button,
  Icon,
} from '@socialchorus/shared-ui-components';

import { usePatronSelector } from '../../../common/use-patron-selector';
import { contentSelectors } from '../../../models/content';
import useFeatureFlag from '../../../common/use-feature-flag';
import { Feature } from '../../../models/features/features';
import { usePdfExport } from '../../../hooks/use-pdf-export';
import { Spinner } from '../../ui/spinner';

interface ContentActionPdfProps {
  contentId: number;
  showText?: boolean;
  wideMode?: boolean;
  container?: HTMLElement | null;
}

const ContentActionPdfWrapper: React.FC<ContentActionPdfProps> = ({
  contentId,
  showText = false,
  wideMode = false,
  container,
}) => {
  const pdfExportFlagEnabled = useFeatureFlag(Feature.PDF_EXPORT);
  const pdfExportEnabled = usePatronSelector((state) =>
    contentSelectors.getContentPdfExportEnabled(state, { contentId })
  );
  const contentTitle = usePatronSelector((state) =>
    contentSelectors.getContentFunctionalTitle(state, { contentId })
  );

  const { exportPdf, generating, error } = usePdfExport(contentId);

  const { t } = useTranslation(undefined, { useSuspense: false });

  if (!pdfExportFlagEnabled || !pdfExportEnabled) return null;

  const displayText = showText || wideMode;
  const safeTitle =
    contentTitle?.trim() ||
    t('content.export_pdf_untitled', { defaultValue: 'Untitled' });
  const actionLabel = t('content.export_pdf_action', {
    title: safeTitle,
    defaultValue: `Export ${safeTitle} as PDF`,
  });
  const buttonLabel = t('content.export_pdf', { defaultValue: 'Export PDF' });
  const generatingLabel = t('content.export_pdf_generating', {
    defaultValue: 'Generating PDF…',
  });
  const errorLabel = t('content.export_pdf_error', {
    defaultValue: 'Export failed — try again',
  });

  // Surface failures: the export is fire-and-forget, so without this the button
  // would just silently re-enable. error clears on the next click (the hook
  // resets it when exportPdf starts).
  let currentLabel = buttonLabel;
  let currentIconName = 'download';
  if (generating) {
    currentLabel = generatingLabel;
  } else if (error) {
    currentLabel = errorLabel;
    currentIconName = 'error_outline';
  }

  // While generating, show the shared spinner in place of the icon.
  const iconNode = generating ? (
    <Spinner loading />
  ) : (
    <Icon>{currentIconName}</Icon>
  );

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!generating) exportPdf();
  };

  return displayText ? (
    <Button
      variant="text"
      leftIcon={iconNode}
      onClick={handleClick}
      aria-label={generating ? generatingLabel : actionLabel}
      label={currentLabel}
      disabled={generating}
    />
  ) : (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <IconButton
          aria-label={generating || error ? currentLabel : actionLabel}
          icon={iconNode}
          onClick={handleClick}
          disabled={generating}
        />
      </TooltipTrigger>
      <TooltipContent description={currentLabel} container={container} />
    </TooltipRoot>
  );
};

export default ContentActionPdfWrapper;
